import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Download, 
  Filter, 
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  FileText,
  Calendar,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BOROUGHS = ['MANHATTAN', 'BROOKLYN', 'BRONX', 'QUEENS', 'STATEN ISLAND'];
const JOB_TYPES = [
  { value: 'A1', label: 'Alteration Type 1 (Major)' },
  { value: 'A2', label: 'Alteration Type 2 (Minor)' },
  { value: 'A3', label: 'Alteration Type 3 (Cosmetic)' },
  { value: 'NB', label: 'New Building' },
  { value: 'DM', label: 'Demolition' },
];

export default function SCADatasetPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  
  // Filters
  const [borough, setBorough] = useState('');
  const [jobType, setJobType] = useState('');
  const [status, setStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', page * limit);
      if (borough) params.append('borough', borough);
      if (jobType) params.append('job_type', jobType);
      if (status) params.append('status', status);
      
      const res = await api().get(`/export/custom/school-construction-authority?${params.toString()}`);
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch SCA data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api, page, limit, borough, jobType, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '5000');
      if (borough) params.append('borough', borough);
      if (jobType) params.append('job_type', jobType);
      if (status) params.append('status', status);
      
      const res = await api().get(`/export/custom/school-construction-authority/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sca_filings_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setBorough('');
    setJobType('');
    setStatus('');
    setPage(0);
  };

  const hasFilters = borough || jobType || status;
  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Building2 className="w-7 h-7 text-cyan-400" />
              School Construction Authority
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              DOB filings and permit applications for NYC School Construction Authority
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`text-sm ${hasFilters ? 'border-cyan-500 text-cyan-400' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasFilters && <span className="ml-2 bg-cyan-500 text-white text-xs px-1.5 py-0.5 rounded">Active</span>}
            </Button>
            
            <Button
              onClick={handleExport}
              disabled={exporting || total === 0}
              className="btn-primary text-sm"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="glass-card border-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white">Filter Results</h3>
              {hasFilters && (
                <button onClick={clearFilters} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Borough Filter */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Borough</label>
                <select
                  value={borough}
                  onChange={(e) => { setBorough(e.target.value); setPage(0); }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="">All Boroughs</option>
                  {BOROUGHS.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              
              {/* Job Type Filter */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Job Type</label>
                <select
                  value={jobType}
                  onChange={(e) => { setJobType(e.target.value); setPage(0); }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="">All Types</option>
                  {JOB_TYPES.map(jt => (
                    <option key={jt.value} value={jt.value}>{jt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter */}
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="">All Statuses</option>
                  <option value="PERMIT ISSUED">Permit Issued</option>
                  <option value="SIGNED OFF">Signed Off</option>
                  <option value="IN PROCESS">In Process</option>
                  <option value="APPROVED">Approved</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="flex items-center justify-between mb-4 text-sm">
          <div className="text-zinc-400">
            Showing <span className="text-white font-medium">{data.length}</span> of{' '}
            <span className="text-white font-medium">{total.toLocaleString()}</span> records
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-zinc-400 text-xs">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="glass-card border-white/10 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-800/50 text-left">
                    <th className="px-4 py-3 text-zinc-400 font-medium">Job #</th>
                    <th className="px-4 py-3 text-zinc-400 font-medium">Address</th>
                    <th className="px-4 py-3 text-zinc-400 font-medium">Borough</th>
                    <th className="px-4 py-3 text-zinc-400 font-medium">Type</th>
                    <th className="px-4 py-3 text-zinc-400 font-medium">Status</th>
                    <th className="px-4 py-3 text-zinc-400 font-medium">Last Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {data.map((rec, idx) => (
                    <tr key={`${rec.job_number}-${idx}`} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-cyan-400 font-mono text-xs">{rec.job_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-white">{rec.address || 'N/A'}</div>
                            <div className="text-xs text-zinc-500">
                              Block {rec.block}, Lot {rec.lot}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">{rec.borough}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded text-xs">
                          <FileText className="w-3 h-3" />
                          {rec.job_type_description || rec.job_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                          rec.job_status_description?.includes('SIGNED OFF') 
                            ? 'bg-emerald-500/20 text-emerald-300'
                            : rec.job_status_description?.includes('PERMIT ISSUED')
                            ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }`}>
                          {rec.job_status_description || rec.job_status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-zinc-400 text-xs">
                          <Calendar className="w-3 h-3" />
                          {rec.latest_action_date 
                            ? new Date(rec.latest_action_date).toLocaleDateString() 
                            : 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card border-white/10 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white">{total.toLocaleString()}</div>
            <div className="text-xs text-zinc-500">Total Records</div>
          </div>
          <div className="glass-card border-white/10 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-cyan-400">5</div>
            <div className="text-xs text-zinc-500">Boroughs</div>
          </div>
          <div className="glass-card border-white/10 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">28K+</div>
            <div className="text-xs text-zinc-500">School Projects</div>
          </div>
          <div className="glass-card border-white/10 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">Live</div>
            <div className="text-xs text-zinc-500">NYC Open Data</div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
